import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, Button, IconButton, Divider,
} from '@mui/material';
import { Close, PriceChange } from '@mui/icons-material';

const fmt = (v) =>
  `₹${parseFloat(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Shown when the server rejects payment with `prices_recalculated` (a price
// list revision or MRP-cap moved between screen and charge) or the
// pre-existing `promotions_recalculated` (a stale promotion was dropped).
// Same dialog for both — the invariant is the same: money never moves at a
// price the screen didn't show, so we show the new numbers and let the
// cashier explicitly confirm before retrying.
const BillUpdateConfirmDialog = ({ open, billUpdate, onConfirm, onCancel, loading }) => {
  if (!billUpdate) return null;
  const { code, message, priceChanges = [], newOrder, oldTotal } = billUpdate;
  const newTotal = parseFloat(newOrder?.grand_total || 0);

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onCancel}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: '12px', m: { xs: 2, sm: 3 } } }}
    >
      <DialogTitle sx={{ px: 2.5, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <Box sx={{ width: 32, height: 32, borderRadius: '8px', bgcolor: '#FFFBEB', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <PriceChange sx={{ fontSize: 18, color: '#92400E' }} />
          </Box>
          <Typography fontWeight={800} fontSize="0.95rem" color="#1E293B" noWrap>Bill Updated</Typography>
        </Box>
        <IconButton size="small" onClick={onCancel} disabled={loading} sx={{ color: '#94A3B8', mr: -0.5, flexShrink: 0 }}>
          <Close sx={{ fontSize: 18 }} />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ px: 2.5, py: 2 }}>
        <Typography sx={{ fontSize: '0.8rem', color: '#475569', mb: 1.5 }}>
          {message || 'Prices changed since this bill was created. Review the new total before collecting payment.'}
        </Typography>

        {code === 'prices_recalculated' && priceChanges.length > 0 && (
          <Box sx={{ border: '1px solid #E2E8F0', borderRadius: '10px', overflow: 'hidden', mb: 1.5 }}>
            {priceChanges.map((c, idx) => (
              <Box
                key={c.order_item_id ?? idx}
                sx={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1,
                  px: 1.5, py: 1,
                  borderTop: idx === 0 ? 'none' : '1px solid #E2E8F0',
                  bgcolor: '#F8FAFC',
                }}
              >
                <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#1E293B', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {c.product_name}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, flexShrink: 0 }}>
                  <Typography sx={{ fontSize: '0.72rem', color: '#94A3B8', textDecoration: 'line-through' }}>{fmt(c.old_price)}</Typography>
                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: parseFloat(c.new_price) > parseFloat(c.old_price) ? '#DC2626' : '#16A34A' }}>
                    {fmt(c.new_price)}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: '#EEF2FF', borderRadius: '10px', px: 1.5, py: 1.25 }}>
          <Box>
            <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#64748B', letterSpacing: '0.04rem' }}>NEW TOTAL</Typography>
            {oldTotal != null && parseFloat(oldTotal) !== newTotal && (
              <Typography sx={{ fontSize: '0.7rem', color: '#94A3B8', textDecoration: 'line-through' }}>was {fmt(oldTotal)}</Typography>
            )}
          </Box>
          <Typography sx={{ fontSize: '1.3rem', fontWeight: 900, color: '#4F46E5' }}>{fmt(newTotal)}</Typography>
        </Box>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 2.5, py: 2 }}>
        <Button onClick={onCancel} disabled={loading} sx={{ textTransform: 'none', fontWeight: 600, color: '#64748B' }}>
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          disabled={loading}
          variant="contained"
          sx={{ bgcolor: '#4F46E5', '&:hover': { bgcolor: '#4338CA' }, borderRadius: '8px', boxShadow: 'none', textTransform: 'none', fontWeight: 700, px: 2.5 }}
        >
          Confirm &amp; Collect
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BillUpdateConfirmDialog;
